import API from './API';

// -----------------------------------------------------------------------------
//  Paginazione LATO SERVER.
//
//  Il backend applica un tetto alle righe restituite (DEFAULT_CRUD_LIMIT=1000,
//  MAX_CRUD_LIMIT=2000, vedi src/crud.config.ts): una GET senza ?page scarica
//  al massimo 1000 record e i successivi spariscono SENZA errore.
//  Passando ?page= la risposta diventa un oggetto con i metadati:
//      { data: [...], count, total, page, pageCount }
//  invece del semplice array. Le funzioni "*Paged" qui sotto normalizzano
//  sempre in { rows, total } così le pagine non devono preoccuparsene.
// -----------------------------------------------------------------------------

const PAGE_SIZE = 25;

// La risposta cambia forma a seconda che la richiesta sia paginata o meno:
// qui la riduciamo a un formato unico.
function normalize(response, fallbackPage = 1) {
  const body = response?.data;
  if (Array.isArray(body)) {
    return { rows: body, total: body.length, page: fallbackPage, pageCount: 1 };
  }
  return {
    rows: body?.data ?? [],
    total: body?.total ?? 0,
    page: body?.page ?? fallbackPage,
    pageCount: body?.pageCount ?? 1,
  };
}

// Righe da non mostrare mai: utenze di servizio e cancellati logici.
const EXCLUDE_SYSTEM = [
  { role: { $ne: 'admin' } },
  { mail: { $ne: 'Eliminato' } },
];

// "Azienda" = ha una ragione sociale valorizzata; "privato" = non ce l'ha.
// Il campo può essere NULL oppure stringa vuota: vanno gestiti entrambi.
const IS_COMPANY = [{ company_name: { $notnull: true } }, { company_name: { $ne: '' } }];
const IS_PRIVATE = {
  $or: [{ company_name: { $isnull: true } }, { company_name: { $eq: '' } }],
};

// Ricerca testuale su più campi ($contL = "contiene", case-insensitive).
function searchClause(term) {
  const t = (term || '').trim();
  if (!t) return null;
  return {
    $or: [
      { name: { $contL: t } },
      { surname: { $contL: t } },
      { company_name: { $contL: t } },
      { mail: { $contL: t } },
      { telephone_number: { $contL: t } },
      { city: { $contL: t } },
      // Serve il join `cars` nella richiesta (vedi getCustomersPaged).
      { 'cars.targa': { $contL: t } },
    ],
  };
}

// Compone le condizioni del segmento in un unico filtro `s` (JSON).
// Filtrare qui invece che in JavaScript è ciò che permette di NON scaricare
// l'intera tabella per poi buttarne via il 95%.
function buildSegmentQuery(segment, { search, city }) {
  const and = [...EXCLUDE_SYSTEM];

  if (segment === 'aziende') {
    and.push(...IS_COMPANY);
  } else {
    and.push(IS_PRIVATE, { is_alive: { $eq: true } });
  }

  if (city) and.push({ city: { $eq: city } });
  const s = searchClause(search);
  if (s) and.push(s);

  return JSON.stringify({ $and: and });
}

export default {
  // ---------------------------------------------------------------- LISTA ---
  // Elenco paginato. `segment`: 'clienti' (privati) | 'aziende'.
  // Ritorna { rows, total, page, pageCount }.
  // `sort` è opzionale ("campo,ASC|DESC"): l'ordinamento lo fa il DATABASE,
  // non il browser — altrimenti si ordinerebbero solo le 25 righe della
  // pagina corrente invece dell'intero elenco.
  getCustomersPaged({
    page = 1,
    limit = PAGE_SIZE,
    segment = 'clienti',
    search = '',
    city = '',
    sort = '',
  } = {}) {
    const defaultSort = segment === 'aziende' ? 'company_name,ASC' : 'surname,ASC';
    return API.get('/customer', {
      params: {
        page,
        limit,
        sort: sort || defaultSort,
        // Le auto servono alla ricerca per targa.
        join: ['cars'],
        s: buildSegmentQuery(segment, { search, city }),
      },
    }).then((r) => normalize(r, page));
  },

  // Solo il conteggio di un segmento (per i KPI in alto): chiede 1 riga.
  countSegment(segment) {
    return API.get('/customer', {
      params: { page: 1, limit: 1, s: buildSegmentQuery(segment, {}) },
    }).then((r) => normalize(r).total);
  },

  // Clienti con un pacchetto valet ancora DA CONFERMARE.
  //
  // Si parte da /minute (= i pacchetti valet) e non da /customer: così basta
  // UNA chiamata invece di una per ogni cliente. Il controller Minute espone
  // i join 'car' e 'car.customer', quindi anagrafica e auto arrivano insieme.
  // Pacchetti valet, dal più recente. `soloDaConfermare` restringe a quelli
  // ancora in attesa; senza, si vedono TUTTI — è la scheda «Valet».
  getPendingValetPaged({ page = 1, limit = PAGE_SIZE, soloDaConfermare = false } = {}) {
    return API.get('/minute', {
      params: {
        page,
        limit,
        ...(soloDaConfermare ? { filter: 'accepted||$eq||false' } : {}),
        join: ['car', 'car.customer'],
        sort: 'data_reg,DESC',
      },
    }).then((r) => normalize(r, page));
  },

  // Solo il conteggio (per il badge/KPI): chiede 1 riga e legge `total`.
  // Senza `soloDaConfermare` conta TUTTI i pacchetti.
  countPendingValet({ soloDaConfermare = true } = {}) {
    return API.get('/minute', {
      params: {
        page: 1,
        limit: 1,
        ...(soloDaConfermare ? { filter: 'accepted||$eq||false' } : {}),
      },
    }).then((r) => normalize(r).total);
  },

  // Id dei clienti che hanno almeno un valet da confermare.
  // Serve a segnalarli nella lista principale: sono pochi (sono "in coda"),
  // quindi una sola chiamata leggera basta a marcare le righe.
  getPendingValetCustomerIds({ limit = 200 } = {}) {
    return API.get('/minute', {
      params: {
        page: 1,
        limit,
        filter: 'accepted||$eq||false',
        join: ['car', 'car.customer'],
      },
    }).then((r) => {
      const ids = new Set();
      normalize(r).rows.forEach((m) => {
        const id = m?.car?.customer?.id;
        if (id) ids.add(id);
      });
      return ids;
    });
  },

  // -------------------------------------------------------------- DETTAGLIO ---
  // Con i partner: servono a scegliere di chi è un pacchetto valet nuovo.
  getCustomerById(id) {
    return API.get('/customer/' + id, { params: { join: ['partners'] } });
  },

  // Booking del cliente, dal più recente. Il join porta la sede e il partner
  // così la tabella dello storico non deve fare altre chiamate.
  getCustomerBookings(customerId, { limit = 20 } = {}) {
    return API.get('/booking', {
      params: {
        filter: `customer.id||$eq||${customerId}`,
        join: ['customer', 'headquarter', 'headquarter.partner'],
        sort: 'data,DESC',
        limit,
      },
    });
  },

  // ------------------------------------------------------------------ CRUD ---
  updCustomer(customer) {
    return API.patch('/customer/' + customer.id, customer);
  },

  addCustomer(customer) {
    // 400/409 (dati non validi, mail già usata) sono gestiti dalle pagine
    return API.post('/auth/register/', customer, { skipErrorToast: true });
  },

  // Recupera un cliente dalla sua email: la mail è univoca (il backend
  // risponde 409 se già usata), quindi identifica il cliente in modo affidabile.
  findByMail(mail) {
    return API.get('/customer', {
      params: { filter: `mail||$eq||${mail}`, limit: 1 },
    }).then((r) => normalize(r).rows[0] || null);
  },

  deleteCustomer(id) {
    return API.get('/customer/delete/' + id);
  },

  getCustomerByCar(id) {
    return API.get(`/car?filter=id||$eq||${id}`, {
      params: { join: ['customer'] },
    });
  },
};
